
import React, { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';
import { FaMapMarkerAlt, FaBoxes, FaSearch, FaChevronDown, FaChevronUp, FaClipboardList, FaWarehouse } from 'react-icons/fa';
import CompletedOrders from './CompletedOrders';
import CancelledOrders from './CancelledOrders';
import ReturnReplaceRequests from './ReturnReplaceRequests';
import AssignedPickups from '../pages/AssignedPickups';
import CompletedCancelledRequests from './CompletedCancelledRequests';

const noop = () => {};

const ORDER_MANAGEMENT_TABS = [
    { id: 'completedOrders', label: 'Completed' },
    { id: 'cancelledOrders', label: 'Cancelled' },
    { id: 'returnRequests', label: 'Return Requests' },
    { id: 'pickupTasks', label: 'Pickup Tasks' },
    { id: 'completedReturns', label: 'Completed Returns' },
    { id: 'cancelledReturns', label: 'Cancelled Returns' }
];

const FranchiseStockManagement = ({ franchiseData }) => {
    const [products, setProducts] = useState([]);
    const [franchises, setFranchises] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedLocations, setExpandedLocations] = useState({});
    const [stockEdits, setStockEdits] = useState({});
    const [savingRowKey, setSavingRowKey] = useState(null);
    const [cardViews, setCardViews] = useState({}); // { [locName]: 'inventory' | 'orders' }
    const [cardOrderTabs, setCardOrderTabs] = useState({}); // { [locName]: orderTabId }

    useEffect(() => {
        fetchProducts();
        if (!franchiseData) fetchFranchises(); // Admin view: also load franchise directory
    }, []);

    const fetchProducts = async () => {
        try {
            const response = await apiClient.get('/products');
            setProducts(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch products:', error);
            setLoading(false);
        }
    };

    const fetchFranchises = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await apiClient.get('/franchise', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFranchises(response.data);
        } catch (error) {
            console.error('Failed to fetch franchises:', error);
        }
    };

    // Organize products by location
    const getLocationsData = () => {
        const locations = {};

        products.forEach(product => {
            if (product.pincodePricing && product.pincodePricing.length > 0) {
                // If this is a franchise user, only show products that this franchise actually manages
                // (i.e. at least one pincode row falls within their covered pincodes). Once a product
                // qualifies, show ALL of its pincode/stock rows so the franchise sees everything they added.
                if (franchiseData) {
                    const productMatchesFranchise = product.pincodePricing.some(pricing => {
                        const rowPincodes = pricing.pincode ? pricing.pincode.split(',').map(pin => pin.trim()) : [];
                        return rowPincodes.some(pin => franchiseData.pincodesCovered?.includes(pin));
                    });
                    if (!productMatchesFranchise) return;
                }

                // Admin mode: find every franchise this product belongs to (any row's pincode falls
                // within that franchise's coverage). If found, ALL of the product's rows go under that
                // franchise's own card (same rule the franchise dashboard uses for itself), so the card
                // isn't split/mixed with an unrelated district card. Products matching no franchise still
                // fall back to a location-text card, same as before.
                const matchingFranchises = !franchiseData
                    ? franchises.filter(f => product.pincodePricing.some(p => f.pincodesCovered?.includes(p.pincode)))
                    : [];

                if (!franchiseData && matchingFranchises.length > 0) {
                    matchingFranchises.forEach(matchedFranchise => {
                        const locKey = `franchise-${matchedFranchise._id}`;
                        if (!locations[locKey]) {
                            locations[locKey] = {
                                name: matchedFranchise.name,
                                pincodes: new Set(),
                                products: [],
                                franchise: matchedFranchise
                            };
                        }
                        product.pincodePricing.forEach(pricing => {
                            locations[locKey].pincodes.add(pricing.pincode);
                            locations[locKey].products.push({
                                ...product,
                                localPrice: pricing.price,
                                localOriginalPrice: pricing.originalPrice,
                                localStock: pricing.inventory,
                                pincode: pricing.pincode,
                                localSize: pricing.size,
                                localPricingId: pricing._id
                            });
                        });
                    });
                } else {
                    product.pincodePricing.forEach(pricing => {
                        const locKey = pricing.location || pricing.pincode || 'Unknown Location';
                        if (!locations[locKey]) {
                            locations[locKey] = {
                                name: locKey,
                                pincodes: new Set(),
                                products: [],
                                franchise: null
                            };
                        }
                        locations[locKey].pincodes.add(pricing.pincode);
                        locations[locKey].products.push({
                            ...product,
                            localPrice: pricing.price,
                            localOriginalPrice: pricing.originalPrice,
                            localStock: pricing.inventory,
                            pincode: pricing.pincode,
                            localSize: pricing.size,
                            localPricingId: pricing._id
                        });
                    });
                }
            } else {
                if (franchiseData) return; // Franchise users do not manage General Inventory

                // If no pincode pricing, maybe it's general stock?
                const locKey = 'General Inventory';
                if (!locations[locKey]) {
                    locations[locKey] = {
                        name: locKey,
                        pincodes: new Set(['Global']),
                        products: []
                    };
                }
                locations[locKey].products.push({
                    ...product,
                    localPrice: product.variants?.[0]?.price || 'N/A',
                    localOriginalPrice: product.variants?.[0]?.originalPrice || null,
                    localStock: product.variants?.reduce((acc, v) => acc + (v.countInStock || 0), 0) || 0,
                    pincode: 'Global',
                    localSize: product.variants?.[0]?.size || 'N/A'
                });
            }
        });

        return Object.values(locations).sort((a, b) => a.name.localeCompare(b.name));
    };

    const toggleLocation = (locName) => {
        setExpandedLocations(prev => ({
            ...prev,
            [locName]: !prev[locName]
        }));
    };

    const handleStockSave = async (prod) => {
        const rowKey = `${prod._id}-${prod.localPricingId}`;
        const newValue = stockEdits[rowKey];
        if (newValue === undefined || newValue === '' || Number(newValue) < 0) return;

        setSavingRowKey(rowKey);
        try {
            const token = localStorage.getItem('token');
            await apiClient.patch(`/products/${prod._id}/stock`, {
                rowId: prod.localPricingId,
                inventory: Number(newValue)
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStockEdits(prev => {
                const updated = { ...prev };
                delete updated[rowKey];
                return updated;
            });
            await fetchProducts();
        } catch (error) {
            console.error('Failed to update stock:', error);
            alert(error.response?.data?.message || 'Failed to update stock.');
        } finally {
            setSavingRowKey(null);
        }
    };

    if (loading) return <div className="p-8 text-center bg-white rounded-lg shadow">Loading inventory data...</div>;

    const locationsData = getLocationsData();
    const filteredLocations = locationsData.filter(loc =>
        loc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        loc.products.some(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                    <div className="flex flex-col items-center justify-center w-full">
                        <div className="flex items-center justify-center gap-3 mb-2">
                            <div className="h-8 w-2 bg-green-500 rounded-full"></div>
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 tracking-tight text-center flex items-center gap-2">
                                <FaMapMarkerAlt className="text-red-500" />
                                Franchise & Location Inventory
                            </h1>
                        </div>
                        <p className="text-sm text-gray-500 mt-1 text-center">Monitor product availability across all delivery points</p>
                    </div>
                    <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search location or product..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none w-full md:w-80 shadow-sm"
                        />
                    </div>
                </div>

                <div className="grid gap-6">
                    {filteredLocations.length > 0 ? filteredLocations.map((loc) => (
                        <div key={loc.name} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                            <div
                                onClick={() => toggleLocation(loc.name)}
                                className="p-4 md:p-5 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600">
                                        <FaMapMarkerAlt />
                                    </div>
                                    <div>
                                        {loc.franchise ? (
                                            <>
                                                <div className="flex items-center gap-2">
                                                    <h2 className="text-lg font-semibold text-gray-800 font-sans tracking-tight">{loc.franchise.name}</h2>
                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${loc.franchise.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                        {loc.franchise.status}
                                                    </span>
                                                </div>
                                                <p className="text-[11px] text-gray-400 font-normal font-sans tracking-wider">
                                                    {loc.franchise.owner?.name && `Owner: ${loc.franchise.owner.name}`}
                                                    {loc.franchise.location?.city && ` · ${loc.franchise.location.city}, ${loc.franchise.location.state}`}
                                                </p>
                                                <p className="text-[11px] text-gray-400 font-normal font-sans tracking-wider uppercase">
                                                    PINCODES: {Array.from(loc.pincodes).join(', ')}
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <h2 className="text-lg font-semibold text-gray-800 font-sans tracking-tight">{loc.name}</h2>
                                                <p className="text-[11px] text-gray-400 font-normal font-sans tracking-wider uppercase">
                                                    PINCODES: {Array.from(loc.pincodes).join(', ')}
                                                </p>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-6">
                                    <div className="hidden sm:block text-right">
                                        <div className="text-xs text-gray-400 uppercase font-bold tracking-wider">Total Products</div>
                                        <div className="text-lg font-bold text-gray-700">{loc.products.length}</div>
                                    </div>
                                    <div className="w-px h-8 bg-gray-200 hidden sm:block"></div>
                                    <div className="bg-gray-100 p-2 rounded-full text-gray-500">
                                        {expandedLocations[loc.name] ? <FaChevronUp /> : <FaChevronDown />}
                                    </div>
                                </div>
                            </div>

                            {loc.franchise && (
                                <div className="px-4 md:px-5 pb-3 flex bg-gray-50/50 border-t border-gray-100">
                                    <div className="flex bg-gray-100 p-1 rounded-lg mt-3">
                                        <button
                                            onClick={() => {
                                                setCardViews(prev => ({ ...prev, [loc.name]: 'inventory' }));
                                                setExpandedLocations(prev => ({ ...prev, [loc.name]: true }));
                                            }}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wide transition-all ${(cardViews[loc.name] || 'inventory') === 'inventory' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                        >
                                            <FaWarehouse size={11} /> Inventory
                                        </button>
                                        <button
                                            onClick={() => {
                                                setCardViews(prev => ({ ...prev, [loc.name]: 'orders' }));
                                                setExpandedLocations(prev => ({ ...prev, [loc.name]: true }));
                                            }}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wide transition-all ${cardViews[loc.name] === 'orders' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                        >
                                            <FaClipboardList size={11} /> Order Management
                                        </button>
                                    </div>
                                </div>
                            )}

                            {expandedLocations[loc.name] && cardViews[loc.name] === 'orders' && loc.franchise && (
                                <div className="border-t border-gray-100 bg-white p-4 md:p-5">
                                    <div className="flex flex-wrap gap-2 mb-5">
                                        {ORDER_MANAGEMENT_TABS.map(tab => (
                                            <button
                                                key={tab.id}
                                                onClick={() => setCardOrderTabs(prev => ({ ...prev, [loc.name]: tab.id }))}
                                                className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-all ${(cardOrderTabs[loc.name] || 'completedOrders') === tab.id ? 'bg-emerald-600 text-white shadow-sm' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                                            >
                                                {tab.label}
                                            </button>
                                        ))}
                                    </div>

                                    {(cardOrderTabs[loc.name] || 'completedOrders') === 'completedOrders' && (
                                        <CompletedOrders franchiseId={loc.franchise._id} />
                                    )}
                                    {cardOrderTabs[loc.name] === 'cancelledOrders' && (
                                        <CancelledOrders franchiseId={loc.franchise._id} />
                                    )}
                                    {cardOrderTabs[loc.name] === 'returnRequests' && (
                                        <ReturnReplaceRequests deliveryPartners={[]} setActiveTab={noop} setRefreshFlag={noop} franchiseId={loc.franchise._id} />
                                    )}
                                    {cardOrderTabs[loc.name] === 'pickupTasks' && (
                                        <AssignedPickups setActiveTab={noop} franchiseId={loc.franchise._id} />
                                    )}
                                    {cardOrderTabs[loc.name] === 'completedReturns' && (
                                        <CompletedCancelledRequests type="completed" franchiseId={loc.franchise._id} />
                                    )}
                                    {cardOrderTabs[loc.name] === 'cancelledReturns' && (
                                        <CompletedCancelledRequests type="cancelled" franchiseId={loc.franchise._id} />
                                    )}
                                </div>
                            )}

                            {expandedLocations[loc.name] && cardViews[loc.name] !== 'orders' && (
                                <div className="border-t border-gray-100 bg-white">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <thead className="bg-gray-50 text-gray-500 text-xs uppercase font-bold tracking-wider">
                                                <tr>
                                                    <th className="px-6 py-3">Product</th>
                                                    <th className="px-6 py-3">Pincode</th>
                                                    <th className="px-6 py-3">Size</th>
                                                    <th className="px-6 py-3">Stock Status</th>
                                                    <th className="px-6 py-3 text-right">Selling Price</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {loc.products.map((prod, idx) => (
                                                    <tr key={`${loc.name}-${prod._id}-${idx}`} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center gap-3">
                                                                <img
                                                                    src={prod.images?.[0]}
                                                                    alt={prod.name}
                                                                    className="w-10 h-10 object-cover rounded-md bg-gray-100"
                                                                />
                                                                <div>
                                                                    <div className="text-sm font-bold text-gray-800">{prod.name}</div>
                                                                    <div className="text-[10px] text-gray-400 uppercase font-medium">{prod.brand} | {prod.category?.name}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className="text-[11px] font-medium font-sans bg-blue-50 text-blue-600 px-2 py-1 rounded border border-blue-100 uppercase tracking-tighter">
                                                                {prod.pincode}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className="text-xs font-bold text-gray-600">{prod.localSize}</span>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            {prod.localPricingId ? (
                                                                (() => {
                                                                    const rowKey = `${prod._id}-${prod.localPricingId}`;
                                                                    const editValue = stockEdits[rowKey];
                                                                    const isEditing = editValue !== undefined;
                                                                    const isSaving = savingRowKey === rowKey;
                                                                    return (
                                                                        <div className="flex items-center gap-2">
                                                                            <span className={`w-2 h-2 rounded-full shrink-0 ${prod.localStock > 10 ? 'bg-green-500' : prod.localStock > 0 ? 'bg-orange-400' : 'bg-red-500'}`}></span>
                                                                            <input
                                                                                type="number"
                                                                                min="0"
                                                                                value={isEditing ? editValue : prod.localStock}
                                                                                onChange={(e) => setStockEdits(prev => ({ ...prev, [rowKey]: e.target.value }))}
                                                                                disabled={isSaving}
                                                                                className={`w-20 p-1.5 text-sm font-bold border rounded-lg outline-none focus:ring-2 focus:ring-emerald-500 ${prod.localStock > 10 ? 'text-green-700' : prod.localStock > 0 ? 'text-orange-600' : 'text-red-600'}`}
                                                                            />
                                                                            {isEditing && (
                                                                                <button
                                                                                    onClick={() => handleStockSave(prod)}
                                                                                    disabled={isSaving}
                                                                                    className="text-[10px] font-bold bg-emerald-600 text-white px-2.5 py-1.5 rounded-lg hover:bg-emerald-700 disabled:opacity-60"
                                                                                >
                                                                                    {isSaving ? '...' : 'Save'}
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })()
                                                            ) : (
                                                                <div className="flex items-center gap-2">
                                                                    <span className={`w-2 h-2 rounded-full ${prod.localStock > 10 ? 'bg-green-500' : prod.localStock > 0 ? 'bg-orange-400' : 'bg-red-500'}`}></span>
                                                                    <span className={`text-sm font-bold ${prod.localStock > 10 ? 'text-green-700' : prod.localStock > 0 ? 'text-orange-600' : 'text-red-600'}`}>
                                                                        {prod.localStock <= 0 ? 'Out of Stock' : `${prod.localStock} units`}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 text-right">
                                                            <div className="flex flex-col items-end">
                                                                <div className="text-sm font-bold text-gray-800">₹{prod.localPrice}</div>
                                                                {prod.localOriginalPrice && (
                                                                    <div className="text-[10px] text-gray-400 line-through">₹{prod.localOriginalPrice}</div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {loc.products.length === 0 && (
                                        <div className="p-8 text-center text-gray-400 italic text-sm">
                                            No products assigned to this location yet.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )) : (
                        <div className="bg-white p-12 text-center rounded-xl border-2 border-dashed border-gray-200">
                            <FaBoxes className="text-gray-300 text-4xl mx-auto mb-4" />
                            <h3 className="text-lg font-bold text-gray-600">No results found</h3>
                            <p className="text-gray-400">Try searching for a different location or product</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FranchiseStockManagement;
